import { createContext, useContext, useEffect, useState } from 'react';
import { FeatureFlags } from 'backend/lib/featureFlags/types';
import { getDefaultFeatureFlags } from 'backend/lib/featureFlags/featureFlagService';
type FeatureFlagStatus = 'loading' | 'loaded' | 'error';
interface FeatureFlagContext {
  featureFlags: FeatureFlags;
  status: FeatureFlagStatus;
}
export const FeatureFlagContext = createContext<FeatureFlagContext>({
  featureFlags: getDefaultFeatureFlags(),
  status: 'loading'
});

/**
 * Provides feature flags to the app.
 *
 * @param children - The children of the feature flag provider.
 * @param flags - The feature flags from getServerSideProps if applicable - fallbacks on getDefaultFeatureFlags
 * @returns
 */
const FeatureFlagProvider = ({
  children,
  flags: flagsProp
}: {
  children: React.ReactNode;
  flags?: FeatureFlags;
}) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(flagsProp ?? getDefaultFeatureFlags());
  const [status, setStatus] = useState<FeatureFlagStatus>(flagsProp ? 'loaded' : 'loading');
  useEffect(() => {
    if (status === 'loading') {
      fetch('/api/v1/feature_flags').then(res => res.json()).then(data => {
        setFeatureFlags(data);
        setStatus('loaded');
      }).catch(_ => {
        setStatus('error');
      });
    }
  }, [status]);
  return <FeatureFlagContext.Provider value={{
    featureFlags,
    status
  }} data-sentry-element="unknown" data-sentry-component="FeatureFlagProvider" data-sentry-source-file="FeatureFlagProvider.tsx">
      {children}
    </FeatureFlagContext.Provider>;
};
export const useFeatureFlags = () => {
  return useContext(FeatureFlagContext);
};
export default FeatureFlagProvider;